<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header title="Course Enrollments" icon="fa-chalkboard-teacher"/>

            <div class="columns mt-6">
                <div class="column is-9-fullhd">
                    <p class="has-text-white" v-if="!enrollments"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading your enrollments...</p>
                    <div v-else>
                        <empty-state v-if="activeEnrollments.length === 0" icon="fa-info-circle" title="You have no active course enrollments." text="Browse the Online Course Catalog to get started.">
                            <router-link :to="{name: 'course-catalog'}"><b-button type="is-light" outlined size="is-small"><i class="fad fa-browsers mr-2"></i>Browse the Catalog</b-button></router-link>
                        </empty-state>
                        <div class="columns" v-if="activeEnrollments.length">
                            <div class="column">
                                <enrollment :enrollment="enroll" v-for="enroll in activeEnrollments" class="mb-5"/>
                            </div>
                        </div>
                        <section v-if="completedEnrollments.length !== 0" class="pt-4">
                            <section-header class="mt-6" title="Completed Courses" icon="fa-check-to-slot" :count="completedEnrollments.length"/>

                            <!-- Certification > completion year accordions when the
                                 certification is known; a flat list otherwise. A lone
                                 certification opens by default, several start closed. -->
                            <div v-if="completedGroups">
                                <archive-section v-for="group in completedGroups.groups" :key="'cert-' + group.key"
                                                 level="group" icon="fa-file-certificate"
                                                 :title="group.title" :count="group.count"
                                                 :open="completedGroups.sections === 1">
                                    <archive-section v-for="(year, yi) in group.years" :key="'yr-' + group.key + '-' + year.year"
                                                     :title="year.year" :count="year.enrollments.length" :open="yi === 0">
                                        <enrollment :enrollment="enroll" v-for="enroll in year.enrollments" :key="enroll.id" class="mb-5"/>
                                    </archive-section>
                                </archive-section>
                                <archive-section v-if="completedGroups.ungrouped.length"
                                                 level="group" icon="fa-check-to-slot"
                                                 title="More Completed Courses" :count="completedGroups.ungrouped.length"
                                                 :open="completedGroups.sections === 1">
                                    <enrollment :enrollment="enroll" v-for="enroll in completedGroups.ungrouped" :key="enroll.id" class="mb-5"/>
                                </archive-section>
                            </div>

                            <!-- Flat fallback when no completed enrollment carries a certification -->
                            <template v-else>
                                <enrollment :enrollment="enroll" v-for="enroll in completedEnrollments" class="mb-5"/>
                            </template>
                        </section>

                        <!-- Enrollments retired via the >365-day-expired reactivation path
                             (a new enrollment now carries the course forward) -- effectively
                             dead records at this point, kept visible only as history -->
                        <section v-if="reactivatedEnrollments.length !== 0" class="pt-4">
                            <section-header class="mt-6" title="Reactivated Courses" icon="fa-rotate" :count="reactivatedEnrollments.length"/>
                            <enrollment :enrollment="enroll" v-for="enroll in reactivatedEnrollments" class="mb-5"/>
                        </section>

                        <!--                        <b-tabs>-->
                        <!--                            <b-tab-item label="2022">-->

                        <!--                            </b-tab-item>-->

                        <!--                            <b-tab-item label="2021">-->

                        <!--                            </b-tab-item>-->

                        <!--                            <b-tab-item label="2020">-->

                        <!--                            </b-tab-item>-->
                        <!--                        </b-tabs>-->

                    </div>


                </div>
                <div class="column has-text-white has-text-right is-3-fullhd is-hidden-touch is-hidden-desktop-only is-hidden-widescreen-only">
                    <div class="mb-6 ">
                        <router-link :to="{name: 'course-catalog'}">
                            <b-button type="is-light" outlined><i class="fad fa-browsers mr-3"></i>Browse Available Courses</b-button>
                        </router-link>
                    </div>
                </div>
            </div>

        </template>
    </framework>
</template>

<script>
    import Framework from "../../components/framework/Framework";
    import MainHeader from "../../components/framework/MainHeader";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import Enrollment from "../../components/Enrollment";
    import ArchiveSection from "../../components/framework/ArchiveSection";
    import SectionHeader from "../../components/framework/SectionHeader";
    import EmptyState from "../../components/framework/EmptyState";

    import {mapActions, mapGetters} from "vuex";

    export default {
        components: {ArchiveSection, SectionHeader, EmptyState, Enrollment, Framework, SidePanelNav, MainHeader},
        data: function() {
            return {
                showCompleted: false
            };
        },
        computed: {
            ...mapGetters(['enrollments', 'currentUser']),
            activeEnrollments: function() {
                let enrollments = [];
                for (let i = 0; i < this.enrollments.length; i++) {
                    const enrollment = this.enrollments[i];
                    if (!enrollment.completed_at && !enrollment.reactivated_to_enrollment_id) enrollments.push(enrollment);
                }

                enrollments.sort((a, b) => {
                    return new Date(b.started_at) - new Date(a.started_at);
                });

                return enrollments;
            },
            // Retired via the >365-day-expired reactivation path -- a new enrollment
            // now carries the course forward, this one is effectively dead
            reactivatedEnrollments: function() {
                let enrollments = [];
                for (let i = 0; i < this.enrollments.length; i++) {
                    const enrollment = this.enrollments[i];
                    if (enrollment.reactivated_to_enrollment_id) enrollments.push(enrollment);
                }

                enrollments.sort((a, b) => {
                    return new Date(b.reactivated_at) - new Date(a.reactivated_at);
                });

                return enrollments;
            },
            completedEnrollments: function() {
                let enrollments = [];
                for (let i = 0; i < this.enrollments.length; i++) {
                    const enrollment = this.enrollments[i];
                    if (enrollment.completed_at && !enrollment.reactivated_to_enrollment_id) enrollments.push(enrollment);
                }

                enrollments.sort((a, b) => {
                    return new Date(b.completed_at) - new Date(a.completed_at);
                });

                return enrollments;
            },
            // Completed enrollments grouped by certification > completion year.
            // Categories were tried here first and did not work: a course carries
            // up to 23 of them (they exist so the catalog can be browsed several
            // ways), so "the" category was really just the first one assigned --
            // every 23-category course filed itself under "Course Development".
            // Certification is a genuine single value per enrollment, and the year
            // is what students actually count renewals in.
            // Null when no completed enrollment has a certification (flat fallback).
            completedGroups: function() {
                const completed = this.completedEnrollments;
                const withCert = completed.filter(e => e.certification_id && e.certification_title);
                if (withCert.length === 0) return null;

                const groups = [];
                const groupMap = {};
                for (const enrollment of withCert) {
                    const certId = enrollment.certification_id;
                    if (!groupMap[certId]) {
                        groupMap[certId] = {
                            key: certId,
                            title: enrollment.certification_title,
                            latest: 0,
                            count: 0,
                            years: [],
                            yearMap: {},
                        };
                        groups.push(groupMap[certId]);
                    }

                    const group = groupMap[certId];
                    const completedAt = new Date(enrollment.completed_at);
                    const year = completedAt.getFullYear();
                    if (!group.yearMap[year]) {
                        group.yearMap[year] = {year, enrollments: []};
                        group.years.push(group.yearMap[year]);
                    }
                    group.yearMap[year].enrollments.push(enrollment);
                    group.count++;
                    group.latest = Math.max(group.latest, completedAt.getTime());
                }

                // Most recently active certification first, newest year first
                // within it; completedEnrollments is already newest-first, so the
                // enrollments inside each year keep that order.
                groups.sort((a, b) => b.latest - a.latest);
                for (const group of groups) {
                    group.years.sort((a, b) => b.year - a.year);
                }

                const ungrouped = completed.filter(e => !e.certification_id || !e.certification_title);
                return {groups, ungrouped, sections: groups.length + (ungrouped.length ? 1 : 0)};
            }
        },
        methods: {
            ...mapActions(['getEnrollments'])
        },
        mounted() {
            this.getEnrollments();
        }
    };
</script>

<style lang="scss" scoped>
    h2 {
        font-size: 1.2rem;
        color: white;
        padding-bottom: 0.5rem;
        margin-bottom: 1rem;
        border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

        i {
            color: white;
        }
    }

</style>

<style lang="scss">

</style>
